import { ApolloError } from '@apollo/client';
import { useMemo } from 'react';

import { useGetStudentAnswersOverview } from 'approot/activity-builder/present/use-get-student-answers-overview';
import { useCurrentClassVar } from 'approot/classes/classes.hooks';
import { useGetClassStudentsCount } from 'approot/activity-builder/classroom/classroom-student.hooks';
import type { AnswerFragment } from '../../../present/activity-answers.graphql.generated';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import { TooltipIconWrapperStyled } from 'approot/shared/tooltip/tooltip.style';
import IconExpand from 'approot/shared/icons/icon-expand';
import * as Styled from './component-overlay.style';
import { DataSelector } from 'webclient.constants';

type Props = {
  componentId: string;
  ariaLabel?: string;
  hasFetchedAnswers?: boolean;
  error?: ApolloError;
  borderRadius?: boolean;
  getActiveStudentAnswerCount: (data: AnswerFragment[]) => number;
  onClick?: () => void;
  dataSelector?: DataSelector;
};

const ComponentOverlay = ({
  componentId,
  ariaLabel,
  hasFetchedAnswers,
  error,
  borderRadius,
  getActiveStudentAnswerCount,
  onClick,
  dataSelector,
}: Props) => {
  const currentClass = useCurrentClassVar();
  const userClassId = currentClass?.id;

  const studentsCount = useGetClassStudentsCount({
    fetchPolicy: 'cache-only',
  });

  const { data } = useGetStudentAnswersOverview({
    skip: !userClassId,
    classId: userClassId || null,
    componentId,
  });
  const answerCountData = data?.getClassStudentsAnswers;

  const answersCount = useMemo(() => {
    return answerCountData ? getActiveStudentAnswerCount(answerCountData) : 0;
  }, [answerCountData, getActiveStudentAnswerCount]);

  const isLoading = !hasFetchedAnswers && !error;

  const content = (
    <Styled.ComponentOverlay
      type="button"
      aria-label={ariaLabel || 'View responses'}
      aria-haspopup="true"
      $borderRadius={borderRadius}
      $isLoading={!hasFetchedAnswers && !error}
      onClick={!hasFetchedAnswers ? undefined : onClick}
      data-selector="component-overlay"
    >
      {error ? null : !hasFetchedAnswers ? (
        <Styled.ComponentOverlayButton>
          Loading...
        </Styled.ComponentOverlayButton>
      ) : answersCount > 0 ? (
        <Styled.ComponentOverlayBadge>
          {studentsCount > 0 && (
            <Styled.ComponentOverlayIndicator $isVisible={answersCount > 0}>
              {answersCount > 9 ? '9+' : answersCount}
            </Styled.ComponentOverlayIndicator>
          )}

          <span>Responses</span>
        </Styled.ComponentOverlayBadge>
      ) : null}
    </Styled.ComponentOverlay>
  );

  return (
    <Styled.ComponentOverlayTooltip>
      {isLoading ? (
        content
      ) : (
        <TooltipWrapper
          hoverText="Expand"
          svgContent={
            <TooltipIconWrapperStyled>
              <IconExpand width={20} height={20} />
            </TooltipIconWrapperStyled>
          }
        >
          {content}
        </TooltipWrapper>
      )}
    </Styled.ComponentOverlayTooltip>
  );
};

export default ComponentOverlay;
